// Opening names: the family and variation of a game's opening, as the
// Lichess book writes them. Used by the opening and variation criteria and by
// every count that groups games by opening.
import { pgnHeaders } from './classify.ts';
import { OPENING_FAMILIES } from '../opening-families.ts';

/**
 * Opening names. chess.com names a game's opening by the slug of its ECOUrl:
 * "Caro-Kann-Defense-Advance-Variation...4.Ne5" or "Kings-Gambit-Accepted",
 * the name with its punctuation lost and the moves appended. The family is
 * the Lichess opening book's (`OPENING_FAMILIES`): the longest book family the
 * name starts with, compared folded (case, punctuation, a word's final "s"
 * and so possessives ignored), and written as the book writes it. A name
 * starting with no family takes the longest family found inside it instead,
 * for chess.com's word orders ("Alapin Sicilian Defense", "Closed Sicilian
 * Defense" are the Sicilian Defense). Failing both, the name up to its first
 * family word ("Defense", "Opening", …). "Undefined", chess.com's word for
 * no opening, is none.
 */
const FAMILY_END = new Set(['Defense', 'Defence', 'Opening', 'Game', 'Gambit', 'Attack', 'System', 'Countergambit']);

/** One word folded for comparison: lower case, letters and digits only, a final "s" dropped. */
function foldWord(word: string): string {
  const w = word.toLowerCase().replace(/[^a-z0-9]/g, '');
  return w.length > 2 && w.endsWith('s') ? w.slice(0, -1) : w;
}
const foldWords = (name: string) => name.split(/[\s-]+/).map(foldWord).filter(Boolean);
/** A name folded whole: "King's Gambit" and "Kings Gambit" both "kinggambit". */
export const foldOpening = (name: string) => foldWords(name).join('');

const FAMILIES = new Map(OPENING_FAMILIES.map((f) => [foldOpening(f), f]));

interface FamilyMatch {
  family: string;
  /** Words of the name the family covers: [from, to). */
  from: number;
  to: number;
}

function findFamily(words: string[]): FamilyMatch | undefined {
  const folded = words.map(foldWord);
  let best: FamilyMatch | undefined;
  // The name's start first; only when no family begins it, anywhere inside.
  for (const starts of [[0], folded.map((_, i) => i).slice(1)]) {
    for (const from of starts) {
      let key = '';
      for (let to = from; to < folded.length; to++) {
        key += folded[to];
        const family = FAMILIES.get(key);
        if (family && (!best || foldOpening(family).length > foldOpening(best.family).length)) best = { family, from, to: to + 1 };
      }
    }
    if (best) return best;
  }
  return undefined;
}

/** The words of chess.com's slug before its moves: "...", a move number or a move ends the name. */
function slugWords(url: string): string[] {
  const slug = (url.split('/').filter(Boolean).pop() ?? '').split('...')[0];
  const words: string[] = [];
  for (const w of slug.split('-')) {
    if (/^\d+(\.|$)/.test(w)) break;
    if (/^[KQRBN]?[a-h]?x?[a-h][1-8]/.test(w)) break;
    if (/^O$/.test(w) || /^O-O/.test(w)) break;
    if (w) words.push(w);
  }
  return words;
}

/** The opening's name words from the PGN: chess.com's ECOUrl, else a standard Opening header. */
function nameWords(pgn: string): string[] {
  const headers = pgnHeaders(pgn);
  const words = headers.ECOUrl ? slugWords(headers.ECOUrl) : [];
  const named = words.length ? words : (headers.Opening ?? '').split(/\s+/).filter(Boolean);
  return named.length === 1 && named[0].toLowerCase() === 'undefined' ? [] : named;
}

/**
 * The opening's name from the PGN, its family written as the book writes it:
 * "Caro-Kann-Defense-Advance-Variation...4.Ne5" becomes "Caro-Kann Defense
 * Advance Variation", "Kings-Gambit-Accepted" "King's Gambit Accepted".
 */
export function openingNameOf(pgn: string): string | undefined {
  const words = nameWords(pgn);
  if (!words.length) return undefined;
  const match = findFamily(words);
  if (match && match.from === 0) return [match.family, ...words.slice(match.to)].join(' ');
  return words.join(' ');
}

/** The family of an opening name; see the rule above. */
export function openingFamily(name: string | undefined): string | undefined {
  if (!name || name.toLowerCase() === 'undefined') return undefined;
  const words = name.split(':')[0].trim().split(/\s+/).filter(Boolean);
  const match = findFamily(words);
  if (match) return match.family;
  const end = words.findIndex((w) => FAMILY_END.has(w));
  return (end < 0 ? words : words.slice(0, end + 1)).join(' ') || undefined;
}

/** The family of the game's opening, from its PGN. */
export function openingFamilyOf(pgn: string): string | undefined {
  return openingFamily(openingNameOf(pgn));
}

/**
 * The opening's variation, written as the Lichess book writes names: the
 * family, then the rest of the name after a colon. "Caro-Kann Defense Advance
 * Variation" is "Caro-Kann Defense: Advance Variation", chess.com's word order
 * "Alapin Sicilian Defense" is "Sicilian Defense: Alapin", and a name that is
 * only its family is the family. A name with no known family is kept as is.
 */
export function openingVariation(name: string | undefined): string | undefined {
  if (!name || name.toLowerCase() === 'undefined') return undefined;
  const words = name.split(/\s+/).filter(Boolean);
  const match = findFamily(words);
  if (!match) return words.join(' ') || undefined;
  const rest = [...words.slice(0, match.from), ...words.slice(match.to)]
    .join(' ')
    .replace(/^[\s:,]+|[\s:,]+$/g, '');
  return rest ? `${match.family}: ${rest}` : match.family;
}

/** The variation of the game's opening, from its PGN. */
export function openingVariationOf(pgn: string): string | undefined {
  return openingVariation(openingNameOf(pgn));
}
